import Placeholder from '../Placeholder';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { formatCurrency as formatCurrencyBlack, parseCurrency } from '../../utils/i18n';
import { default as duckify, filterDuck, singleDuck, currentCustomer } from '../../utils/duckify';
import { sortBy, sum } from 'utils/arrays';
import { billingAccounts, billingItems, schoolYears, billingCategoryGroups } from '../../ducks';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import lodash from 'lodash';
import ContactList from 'components/ContactList/ContactList';
import moment from 'moment';
import { ContactRow } from 'components/ContactList/ContactRows';
import classNames from 'classnames';
import withSchoolYear, { defaultBillingSchoolYear } from 'hocs/withSchoolYear';
import SchoolYearSelect from 'components/SchoolYearSelect';
import SelectPicker from 'components/SelectPicker/SelectPicker';

import './styles/BillingItemCalendar.scss';
import InfoTooltip from 'components/shared/InfoTooltip';

import { faCaretLeft } from '@fortawesome/pro-solid-svg-icons/faCaretLeft';
import { faCaretRight } from '@fortawesome/pro-solid-svg-icons/faCaretRight';
import FontAwesome from 'react-fontawesome';
import { addItem, editInstallments, sendStatement } from 'components/shared/BillingItems.jsx';
import { billingCategoryComparator } from '../../ducks/billingCategories';
import MonthlyStatementGenerator from './MonthlyStatementGenerator';

const formatBillingContact = (bc=NO_BILLING_CONTACT) => `${bc.contact.first_name} ${bc.contact.last_name}`;

const makeBillingItemCalendarRow = (props) => {
  const { billingContact, category } = props.contact;

  return (
    <ContactRow
      className={classNames({
        'billing-calendar-row-contact': (billingContact && !category),
        'billing-calendar-row-contact-category': (billingContact && category),
        'billing-calendar-row-total': (!billingContact && !category),
        [`contact-id-${billingContact && billingContact.id}`]: billingContact,
        [`category-id-${category && category.id}`]: category
      })}
      {...props}
    />
  );
};

const formatCurrency = (amount) => {
  amount = parseCurrency(amount);

  return (
    <span className={classNames({
      'negative-number': (amount < 0)
    })}
    >
      {formatCurrencyBlack(amount)}
    </span>
  );
};

/**
 * Gets all the months in between min and max provided months.
 *
 * @param allMonths Array of all months to be considered, in format 'YYYY-MM'
 */
const monthsInRange = (allMonths) => {
  if (allMonths.length <= 0) {
    return [];
  }

  const sortedMonths = allMonths.sort();

  const min = moment(sortedMonths[0]);
  const max = moment(sortedMonths[sortedMonths.length - 1]);

  let ret = [];
  let month = min;
  do {
    ret = [...ret, {
      label: month.format('MMM YYYY'),
      value: month.format('YYYY-MM')
    }];
    month = month.add(1, 'M');
  } while (month <= max);

  return ret;
};

const BillingItemCalendar = (props) => {
  const {items, account, allDucksReady, billingCategoryGroups, onSchoolYearChange, schoolYearId, schoolYears, billingAccountId, dispatch, contactId} = props;
  const isArchived = year => year.archived
  const isSelected = year => year.id == schoolYearId
  const dropdownSchoolYears = schoolYears.filter(year => !isArchived(year) || isSelected(year))
  return <Placeholder.loading ready={allDucksReady} content={() => {
    /**
     * Pretend contact for items unrelated to actual contacts.
     *
     * @type {{last_name: string, payment_plan_name: string, id: string, school_year: null, first_name: string}}
     */
    const NO_BILLING_CONTACT = {
      id: 'imaginary-contact',
      contact: {
        first_name: account.first_name,
        last_name: account.last_name
      },
      payment_plan_name: '',
      school_year: null,
      isNobody: true
    };

    /**
     * Months that have charges in them.
     * @type {*[]}
     */
    let monthsWithBillingItems = items.map(bi => moment(bi.billing_date).format('YYYY-MM')).sort();

    // Tracks which months have had their columns expanded
    const [expandedMonths, setExpandedMonthsUnsafe] = useState({});
    const toggleMonthExpanded = (yearMonth) => {
      setExpandedMonthsUnsafe({
        ...expandedMonths,
        [yearMonth]: !expandedMonths[yearMonth]
      });
    };

    const selectableMonths = monthsInRange(monthsWithBillingItems);

    const [startMonth, setStartMonth] = useState(undefined);
    const [endMonth, setEndMonth] = useState(undefined);

    // Reset start/end months if school year changes
    useEffect(() => {
      setStartMonth(monthsWithBillingItems.length > 0 ? monthsWithBillingItems[0] : undefined);
      setEndMonth(monthsWithBillingItems.length > 0 ? monthsWithBillingItems[monthsWithBillingItems.length - 1] : undefined);
    }, [schoolYearId]);

    const visibleMonths = monthsInRange([startMonth, endMonth]).map(({value}) => value);

    /** Map billing item amounts into something we can use
     *
    {
      contact_1_id: {
        "2021-01" : {
          category_1_id: {
            '2021-01-01': amount,
            '2021-01-05': amount,
            ...
          },
          ...
        },
        "2021_01" : {
          ...
        }
      },
      contact_2_id: {
        ...
      },
      ...
    }
    */
    let dailyBreakdown = {};

    /** Roll up total amount per day for a given contact
     *
    {
      contact_1_id : {
        "2021-01": {
          "2021-01-01" : total,
          "2021-01-02" : total
        },
        ...
      },
      ...
    }
     */
    let contactDailyTotals = {};

    /** Roll up monthly category totals
     *
    {
      contact_1_id: {
        "2021-01": {
          category_1_id: total,
          category_2_id: total,
          ...
        },
        ...
      },
      ...
    }
     */
    let monthlyCategoryTotals = {};

    /** Roll up contact category totals
     *
    {
      contact_1_id: {
        category_1_id: total,
        category_2_id: total,
        ...
      },
      ...
    }
     */
    let contactCategoryTotals = {};

    /** Roll up contact monthly totals
     *
    {
      contact_1_id: {
        '2021-01': amount,
        '2021-02': amount,
        ...
      },
      ...
    }
    */
    let contactCategorySubTotals = {};

    /** Roll up contact charge totals without payments
     *
     {
      contact_1_id: {
        '2021-01': amount,
        '2021-02': amount,
        ...
      },
      ...
    }
     */
    let contactMonthlyTotals = {};

    /** Roll up contact totals
     *
    {
      contact_1_id: amount,
      contact_2_id: amount,
      ...
    }
    */
    const contactTotals = {};

    /** Categories indexed by BillingContact ID and Category ID
     *
    {
      contact_1_id: {
        category_1_id: category_1,
        ...
      },
      ...
    }
    */
    const contactSubTotals = {};

    /** Categories indexed by BillingContact ID and Category ID, excluding payments
     *
     {
      contact_1_id: {
        category_1_id: category_1,
        ...
      },
      ...
    }
     */
    const paymentTotals = {};

    /** Categories indexed by BillingContact ID and Category ID, excluding payments
     *
     {
     contact_1_id: {
     category_1_id: category_1,
     ...
     },
     ...
     }
     */

    let contactCategoryPayments = {};

    /** Roll up contact payments
     *
     {
      contact_1_id: paid,
      contact_2_id: paid,
      ...
    }
     */
    const contactPayments = {};

    /** BillingContacts indexed by ID
     *
    {
      contact_1_id: contact_1,
      ...
    }
    */
    const contactsById = {};

    /**  Days in a given month that have charges on them
     *
    {
      '2021-01': ['2021-01-01', '2021-01-15']
    }
     */
    const daysInMonth = {};

    items.forEach(item => {
      item.billing_item_amounts.filter(bia => {
        const yearMonth = moment(item.billing_date).format('YYYY-MM');
        if (!visibleMonths.includes(yearMonth)) {
          return false;
        }

        if (!schoolYearId) {
          return true;
        }

        if (!bia.billing_contact) {
          return true;
        }

        const biaSchoolYearId =
          bia.billing_contact &&
          bia.billing_contact.school_year &&
          bia.billing_contact.school_year.id;

        return (biaSchoolYearId == schoolYearId);
      }).forEach(bia => {
        const yearMonth = moment(item.billing_date).format('YYYY-MM');
        const yearMonthDay = moment(item.billing_date).format('YYYY-MM-DD');

        const billingContact = bia.billing_contact || NO_BILLING_CONTACT;

        const billingContactId = billingContact.id;
        const category = bia.billing_category;
        const { type } = item;
        const categoryId = category.id;

        const amount = parseFloat(bia.amount);

        if (!contactTotals[billingContactId]) {
          contactTotals[billingContactId] = 0;
        }

        if (!paymentTotals[yearMonth]) {
          paymentTotals[yearMonth] = 0;
        }

        contactTotals[billingContactId] += amount;

        contactCategoryTotals = lodash.merge(contactCategoryTotals, {
          [billingContactId]: { }
        });

        contactCategoryTotals[billingContactId][categoryId] = (contactCategoryTotals[billingContactId][categoryId] || 0) + amount;

        contactCategoryPayments = lodash.merge(contactCategoryPayments, {
          [billingContactId]: { }
        });

        if (!contactPayments[billingContactId]) {
          contactPayments[billingContactId] = 0;
        }

        if (type === 'payment') {
          contactPayments[billingContactId] += amount;
          contactCategoryPayments[billingContactId][categoryId] = (contactCategoryPayments[billingContactId][categoryId] || 0) + amount;
          paymentTotals[yearMonth] += amount;
          return;
          // if it's a payment, it's excluded from the rest of the calculations
        }

        if (!contactSubTotals[billingContactId]) {
          contactSubTotals[billingContactId] = 0;
        }
        contactSubTotals[billingContactId] += amount;

        contactCategorySubTotals = lodash.merge(contactCategorySubTotals, {
          [billingContactId]: { }
        });

        contactCategorySubTotals[billingContactId][categoryId] = (contactCategorySubTotals[billingContactId][categoryId] || 0) + amount;

        dailyBreakdown = lodash.merge(dailyBreakdown, {
          [billingContactId]: {
            [yearMonth]: {
              [categoryId]: { }
            }
          }
        });

        dailyBreakdown[billingContactId][yearMonth][categoryId][yearMonthDay] =
          (dailyBreakdown[billingContactId][yearMonth][categoryId][yearMonthDay] || 0) + amount;

        monthlyCategoryTotals = lodash.merge(monthlyCategoryTotals, {
          [billingContactId]: {
            [yearMonth]: { }
          }
        });
        monthlyCategoryTotals[billingContactId][yearMonth][categoryId] =
          (monthlyCategoryTotals[billingContactId][yearMonth][categoryId] || 0) + amount;

        contactsById[billingContactId] = billingContact;

        contactDailyTotals = lodash.merge(contactDailyTotals, {
          [billingContactId]: {
            [yearMonth]: { }
          }
        });
        contactDailyTotals[billingContactId][yearMonth][yearMonthDay] =
          (contactDailyTotals[billingContactId][yearMonth][yearMonthDay] || 0) + amount;

        contactMonthlyTotals = lodash.merge(contactMonthlyTotals, {
          [billingContactId]: { }
        });

        contactMonthlyTotals[billingContactId][yearMonth] = (contactMonthlyTotals[billingContactId][yearMonth] || 0) + amount;

        daysInMonth[yearMonth] = Array.from(new Set([...(daysInMonth[yearMonth] || []), yearMonthDay])).sort();
      });
    });

    const visibleMonthsAndDays =
      visibleMonths.map((yearMonth) => [
        yearMonth,
        ...(expandedMonths[yearMonth] && daysInMonth[yearMonth] || [])
      ]
    ).flat();

    const todayLine = visibleMonthsAndDays.find((date) =>
      moment(date) >= moment(account.effective_date)
    );

    /* row schema:
    {
      contact: [optional]
      category: [optional]
    }
     */

    const columns = [
      {
        header: 'Name',
        value: ({ billingContact, category, id }) => (
          <td
            className='contact-list-react-name'
            colSpan={(billingContact && !category) ? 1 : 2}
          >
            {(billingContact && category) && category.name}
            {(billingContact && !category) && formatBillingContact(billingContact)}
            {(id === 'totals_row') && 'TOTAL'}
            {(id === 'payment_totals_row') && 'PAYMENT TOTALS'}
          </td>
        )
      },
      {
        header: 'Payment Plan',
        columnClassNames: classNames('payment-plan-name'),
        value: ({ billingContact, category }) => {
          if (!billingContact || category) {
            return null;
          }

          return (
            <td className='payment-plan-name'>
              {(billingContact && !category) && billingContact.payment_plan_name}
            </td>
          );
        }
      },
      ...visibleMonths.map((yearMonth) => {
        return [
          {
            // Monthly Totals
            columnClassNames: classNames(
              'billing-calendar-month-col',
              `billing-calendar-month-col-${yearMonth}`,
              {
                'billing-calendar-today-line': (todayLine === yearMonth),
                'billing-calendar-month-col-collapsed': !expandedMonths[yearMonth],
                'billing-calendar-month-col-expanded': expandedMonths[yearMonth]
              }
            ),
            header: () => <div onClick={() => toggleMonthExpanded(yearMonth)}>
              {moment(yearMonth).format('MMM')}
              <InfoTooltip
                className='column-expander'
                placement='top'
                icon={expandedMonths[yearMonth] ? faCaretLeft : faCaretRight}
                nbsp
                preNbsp
              >
                {expandedMonths[yearMonth] ? 'Hide' : 'Show'}
                Daily Amounts for
                { moment(yearMonth).format('MMMM YYYY') }
              </InfoTooltip>
            </div>,
            value: ({ billingContact, category, id }, { columnClassNames }) =>
              <td className={classNames('month-total', columnClassNames)}>
                {(billingContact && category) && formatCurrency(
                  lodash.get(monthlyCategoryTotals, [billingContact.id, yearMonth, category.id], 0)
                )}
                {(billingContact && !category) && formatCurrency(
                  lodash.get(contactMonthlyTotals, [billingContact.id, yearMonth], 0)
                )}
                {(id === 'totals_row') && formatCurrency(
                  sum(Object.values(contactMonthlyTotals).map(cmt => cmt[yearMonth] || 0).filter(x => x))
                )}
                {(id === 'payment_totals_row') && formatCurrency(
                  lodash.get(paymentTotals, [yearMonth], 0)
                )}
              </td>
          },
          ...(expandedMonths[yearMonth] ?
            daysInMonth[yearMonth] ?
              daysInMonth[yearMonth].map(yearMonthDay => ({
                // Daily Totals (if month expanded)
                columnClassNames: classNames(
                  'billing-calendar-day-col',
                  `billing-calendar-day-col-${yearMonthDay}`,
                  {
                    'billing-calendar-today-line': (todayLine === yearMonthDay)
                  }
                ),
                header: moment(yearMonthDay).format('MM/DD'),
                value: ({ billingContact, category, id }, { columnClassNames }) =>
                  <td className={classNames('daily-total', columnClassNames)}>
                    {(billingContact && category) && formatCurrency(
                      lodash.get(dailyBreakdown, [billingContact.id, yearMonth, category.id, yearMonthDay], 0)
                    )}
                    {(billingContact && !category) && formatCurrency(
                      lodash.get(contactDailyTotals, [billingContact.id, yearMonth, yearMonthDay], 0)
                    )}
                    {(id === 'totals_row') && formatCurrency(
                      sum(Object.values(contactDailyTotals).map(cdt => cdt[yearMonth] && cdt[yearMonth][yearMonthDay] || 0).filter(x => x))
                    )}
                  </td>
              }))
              :
              [{
                // User expanded an empty month.  Show an empty column
                columnClassNames: classNames(
                  'billing-calendar-day-col',
                  'billing-calendar-day-col-empty'
                ),
                header: 'N/A'
              }]
            :
            [])
        ];
      }).flat(),
      {
        header: 'Total Charges',
        columnClassNames: classNames('billing-calendar-subtotal-col'),
        value: ({ billingContact, category, id }) => (
          <td className='billing-calendar-subtotal-col'>
            {(billingContact && category) && formatCurrency(
              lodash.get(contactCategorySubTotals, [billingContact.id, category.id], 0)
            )}
            {(billingContact && !category) && formatCurrency(
              contactSubTotals[billingContact.id] || 0
            )}
            {(id === 'totals_row') && formatCurrency(
              sum(Object.values(contactSubTotals))
            )}
          </td>
        )
      },
      {
        header: 'Paid',
        columnClassNames: classNames('billing-calendar-paid-col'),
        value: ({ billingContact, category, id }) => (
          <td className='billing-calendar-paid-col'>
            {(billingContact && category) && formatCurrency(
              lodash.get(contactCategoryPayments, [billingContact.id, category.id], 0)
            )}
            {(billingContact && !category) && formatCurrency(
              contactPayments[billingContact.id] || 0
            )}
            {(id === 'totals_row') && formatCurrency(
              sum(Object.values(contactPayments))
            )}
          </td>
        )
      },
      {
        header: 'Net Total',
        columnClassNames: classNames('billing-calendar-total-col'),
        value: ({ billingContact, category, id }) => (
          <td className='billing-calendar-total-col'>
            {(billingContact && category) && formatCurrency(
              lodash.get(contactCategoryTotals, [billingContact.id, category.id], 0)
            )}
            {(billingContact && !category) && formatCurrency(
              contactTotals[billingContact.id] || 0
            )}
            {(id === 'totals_row') && formatCurrency(
              sum(Object.values(contactTotals))
            )}
          </td>
        )
      }
    ];

    const sortedContacts = Object.values(contactsById).sort((a, b) => {
      if (a.id === NO_BILLING_CONTACT.id) {
        return 1;
      }
      else if (b.id === NO_BILLING_CONTACT.id) {
        return -1;
      }

      return formatBillingContact(a).localeCompare(formatBillingContact(b));
    });

    const sortedBillingCategories = billingCategoryGroups.sort(sortBy('position')).map(group => [
      group,
      ...group.children.sort(billingCategoryComparator)
    ]).flat();

    const categoryHasVisibleBalanceForContact = (category, billingContact) => {
      const categoryHasBalances = visibleMonths.some(yearMonth =>
        (monthlyCategoryTotals[billingContact.id] &&
          monthlyCategoryTotals[billingContact.id][yearMonth] &&
          monthlyCategoryTotals[billingContact.id][yearMonth][category.id]) ||
        (contactCategoryPayments[billingContact.id] &&
          contactCategoryPayments[billingContact.id][category.id])
      );

      if (categoryHasBalances) {
        return true;
      }

      // If this is a parent category, check for any children with balances
      if (category.parent_id == null) {
        return [...category.children || []]
          .some(child =>
            categoryHasVisibleBalanceForContact(child, billingContact)
          );
      }

      return false;
    };

    const rows = [
      ...sortedContacts.map(billingContact => {
        return [
          // Contact totals row
          {
            id: billingContact.id,
            billingContact
          },
          // per-category
          ...sortedBillingCategories.map(category => {
            // Only show "non-contact" billing categories in the bottom "no contact" section.
            if ((category.contact && billingContact.isNobody) ||
              (!category.contact && !billingContact.isNobody)) {
              return null;
            }

            // Hide categories with no balance
            if (!categoryHasVisibleBalanceForContact(category, billingContact)) {
              return null;
            }

            return {
              id: [billingContact.id, category.id].join('_'),
              billingContact,
              category
            };
          }).filter(x => x)
        ];
      }).flat(),
      // Overall totals row at bottom
      {
        id: 'totals_row'
      },
      {
        id: 'payment_totals_row'
      }
    ];

    return (
      <div className='billing-calendar'>
        <div className='billing-calendar-filters'>
          <b className='inline'>Term &nbsp;</b>
          <SchoolYearSelect
            onSelect={(year) => onSchoolYearChange(year)}
            schoolYearId={schoolYearId}
            schoolYears={dropdownSchoolYears}
            standardButton
          />

          <b className='inline'>Start Month &nbsp;</b>
          <div className='inline start-month-select'>
            <SelectPicker
              options={selectableMonths}
              onChange={setStartMonth}
              value={startMonth}
              standardButton
            />
          </div>

          <b className='inline'>End Month &nbsp;</b>
          <div className='inline end-month-select'>
            <SelectPicker
              options={selectableMonths}
              onChange={setEndMonth}
              value={endMonth}
              standardButton
            />
          </div>
          <ul className='list-inline billing-calendar-actions'>
            <li className='list-inline-item'>
              <button className='btn btn-warning' onClick={() => addItem('charge', props)}>
                <FontAwesome name='plus' />
                Add Charge
              </button>
            </li>
            <li className='list-inline-item'>
              <button className='btn btn-success' onClick={() => addItem('credit', props)}>
                <FontAwesome name='money' />
                Add Credit
              </button>
            </li>
            <li className='list-inline-item'>
              <button className='btn btn-success' onClick={() => addItem('payment', props)}>
                <FontAwesome name='money' />
                Add Payment
              </button>
            </li>
            <li className='list-inline-item'>
              <button className='btn btn-success' onClick={() => editInstallments(props)}>
                <FontAwesome name='reorder' />
                Rearrange Installments
              </button>
            </li>
            <li className='list-inline-item'>
              <MonthlyStatementGenerator billingAccountId={billingAccountId} soloButton />
            </li>
            <li className='list-inline-item'>
              <a className='btn' href={`/billing_accounts/${billingAccountId}/statement`} target='_blank'>
                <FontAwesome name='file-text-o' />
                Summary Statement
              </a>
            </li>
            <li className='list-inline-item'>
              <button className='btn' onClick={() => sendStatement(props)}>
                <FontAwesome name='envelope-o' />
                Send Statement
              </button>
            </li>
          </ul>
        </div>


        <ContactList
          contacts={rows}
          columns={columns}
          emptyMessage='There are no ledger items for the selected time range'
          makeRow={makeBillingItemCalendarRow}
        />
      </div>
    );
  }}
  />;
};

BillingItemCalendar.propTypes = {
  /** BillingItems duck, filtered by current billing account */
  items: PropTypes.array.isRequired,
  /** Current customer duck */
  customer: PropTypes.object.isRequired,
  /** BillingAccount duck */
  account: PropTypes.object.isRequired,
  /** SchoolYears duck */
  schoolYears: PropTypes.array.isRequired,
  /** BillingCategoryGroups duck */
  billingCategoryGroups: PropTypes.array.isRequired
};

export default compose(
  withRouter,
  duckify(props => {
    const filters = {
      per_page: 100000000000000,
      billing_account_id: props.billingAccountId
    };

    return {
      billingCategoryGroups,
      schoolYears,
      customer: currentCustomer,
      account: singleDuck(billingAccounts, props.billingAccountId),
      items: filterDuck(billingItems, filters)
    };
  }),
  withSchoolYear(defaultBillingSchoolYear)
)(BillingItemCalendar);
